/**
 * useRadioFeed — WebSocket hook for SENTINEL radio chatter transcription.
 *
 * Connects to the Python radio_server.py at ws://localhost:8765.
 * Applies client-side keyword tagging via radioTagger.
 * Supports file transcription, mic capture, and LLM analysis.
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import type { RadioTranscript, RadioAnalysis } from '../types'
import { tagTranscript } from '../services/radioTagger'

const WS_URL = 'ws://localhost:8765'
const MAX_TRANSCRIPTS = 500
const RECONNECT_DELAY = 3000

const API_BASE = import.meta.env.VITE_API_URL || '/api'

export interface UseRadioFeedReturn {
  transcripts: RadioTranscript[]
  isConnected: boolean
  isTranscribing: boolean
  statusMessage: string
  startFile: (path: string) => void
  startFileUpload: (file: File) => void
  startMic: () => void
  stop: () => void
  clearTranscripts: () => void
  analyzeRecent: () => Promise<RadioAnalysis | null>
}

export function useRadioFeed(): UseRadioFeedReturn {
  const [transcripts, setTranscripts] = useState<RadioTranscript[]>([])
  const [isConnected, setIsConnected] = useState(false)
  const [isTranscribing, setIsTranscribing] = useState(false)
  const [statusMessage, setStatusMessage] = useState('Disconnected')

  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const mountedRef = useRef(true)

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return

    try {
      const ws = new WebSocket(WS_URL)

      ws.onopen = () => {
        if (!mountedRef.current) return
        setIsConnected(true)
        setStatusMessage('Connected')
      }

      ws.onclose = () => {
        if (!mountedRef.current) return
        setIsConnected(false)
        setIsTranscribing(false)
        setStatusMessage('Disconnected')

        // Auto-reconnect
        reconnectTimerRef.current = setTimeout(() => {
          if (mountedRef.current) connect()
        }, RECONNECT_DELAY)
      }

      ws.onerror = () => {
        if (!mountedRef.current) return
        setStatusMessage('Connection error')
      }

      ws.onmessage = (event) => {
        if (!mountedRef.current) return

        try {
          const msg = JSON.parse(event.data)

          if (msg.type === 'transcript') {
            const { severity, tags } = tagTranscript(msg.text)
            const entry: RadioTranscript = {
              id: msg.id || crypto.randomUUID().slice(0, 8),
              text: msg.text,
              timestamp: msg.timestamp || Date.now() / 1000,
              severity,
              tags,
              confidence: msg.confidence ?? 0.9,
              isFinal: msg.isFinal ?? true,
            }

            setTranscripts((prev) => {
              const next = [...prev, entry]
              return next.length > MAX_TRANSCRIPTS ? next.slice(-MAX_TRANSCRIPTS) : next
            })
            setIsTranscribing(true)
          } else if (msg.type === 'status') {
            setStatusMessage(msg.message)
            if (msg.message === 'Transcription complete' || msg.message === 'Stopped') {
              setIsTranscribing(false)
            }
          } else if (msg.type === 'error') {
            setStatusMessage(`Error: ${msg.message}`)
            setIsTranscribing(false)
          }
        } catch {
          // Ignore malformed messages
        }
      }

      wsRef.current = ws
    } catch {
      setStatusMessage('Failed to connect')
    }
  }, [])

  // Connect on mount
  useEffect(() => {
    mountedRef.current = true
    connect()

    return () => {
      mountedRef.current = false
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current)
      wsRef.current?.close()
    }
  }, [connect])

  const sendMessage = useCallback((data: Record<string, unknown>) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data))
    }
  }, [])

  const startFile = useCallback(
    (path: string) => {
      setIsTranscribing(true)
      sendMessage({ type: 'file', path })
    },
    [sendMessage],
  )

  const startFileUpload = useCallback(
    (file: File) => {
      const reader = new FileReader()
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1]
        setIsTranscribing(true)
        sendMessage({ type: 'file_upload', name: file.name, data: base64 })
      }
      reader.readAsDataURL(file)
    },
    [sendMessage],
  )

  const startMic = useCallback(() => {
    setIsTranscribing(true)
    sendMessage({ type: 'mic' })
  }, [sendMessage])

  const stop = useCallback(() => {
    sendMessage({ type: 'stop' })
    setIsTranscribing(false)
  }, [sendMessage])

  const clearTranscripts = useCallback(() => {
    setTranscripts([])
  }, [])

  const analyzeRecent = useCallback(async (): Promise<RadioAnalysis | null> => {
    const fiveMinAgo = Date.now() / 1000 - 300
    const recent = transcripts
      .filter((t) => t.timestamp >= fiveMinAgo)
      .map((t) => t.text)

    if (recent.length === 0) return null

    try {
      const resp = await fetch(`${API_BASE}/radio/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transcripts: recent }),
      })
      if (!resp.ok) return null
      return (await resp.json()) as RadioAnalysis
    } catch {
      return null
    }
  }, [transcripts])

  return {
    transcripts,
    isConnected,
    isTranscribing,
    statusMessage,
    startFile,
    startFileUpload,
    startMic,
    stop,
    clearTranscripts,
    analyzeRecent,
  }
}
